"use client"

import { ArrowLeft, CreditCard, FileText, Shield, Clock, AlertTriangle, Users, Mail } from "lucide-react"
import Link from "next/link"

export default function RefundPolicyPage() {
  const lastUpdated = "March 1, 2026"
  const effectiveDate = "March 1, 2026"

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border dark:border-white/[0.06] bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/auth/signin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sign In</span>
          </Link>
          <div className="flex items-center gap-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-6 h-6" />
            <span className="font-semibold tracking-wide uppercase text-sm hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-sm inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Refund & Chargeback Policy</h1>
              <p className="text-muted-foreground text-sm">Understanding refunds, chargebacks, and disputed transactions</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Last Updated: {lastUpdated}</span>
            <span>|</span>
            <span>Effective: {effectiveDate}</span>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Overview", href: "#overview" },
              { label: "Merchant Refund Obligations", href: "#merchant-obligations" },
              { label: "Chargeback Process", href: "#chargeback" },
              { label: "Timeframes", href: "#timeframes" },
              { label: "Disputed Transactions", href: "#disputes" },
              { label: "Contact", href: "#contact" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 rounded-lg bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.06] text-sm text-muted-foreground hover:text-foreground hover:border-cyan-500/30 transition-all"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>

        {/* Legal Content */}
        <div className="prose prose-invert prose-gray max-w-none">
          {/* Section 1 - Overview */}
          <section id="overview" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" />
              1. Overview
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                FluxPay is a payment processing platform that facilitates transactions between merchants and their customers. As a payment facilitator, FluxPay does not sell goods or services directly to end consumers. Refund policies are determined by individual merchants.
              </p>
              <p>
                This policy outlines the framework for refunds and chargebacks processed through the FluxPay platform, the responsibilities of merchants, and the rights of cardholders.
              </p>
              <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-cyan-200 text-sm">
                  <strong>Important:</strong> FluxPay acts as a payment processor and facilitator. Refund and return policies are established by the merchant from whom you made your purchase. Please contact the merchant directly for refund requests.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 - Merchant Refund Obligations */}
          <section id="merchant-obligations" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              2. Merchant Refund Obligations
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">2.1 Refund Policy Requirements</h3>
              <p>All merchants using FluxPay&apos;s payment processing services must:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintain a clear and accessible refund policy on their website</li>
                <li>Process legitimate refund requests in a timely manner</li>
                <li>Provide customers with clear information about refund eligibility and processes</li>
                <li>Comply with applicable consumer protection laws in their jurisdiction</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.2 Processing Refunds</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Merchants can initiate refunds through the FluxPay dashboard or API</li>
                <li>Refunds are processed back to the original payment method</li>
                <li>Full and partial refunds are supported</li>
                <li>Refund processing fees may apply as outlined in the merchant agreement</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">2.3 Merchant Responsibilities</h3>
              <p>Merchants are solely responsible for:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Determining refund eligibility based on their own policies</li>
                <li>Communicating refund status to their customers</li>
                <li>Maintaining adequate records of refund requests and decisions</li>
                <li>Ensuring compliance with card network rules regarding refunds</li>
              </ul>
            </div>
          </section>

          {/* Section 3 - Chargeback Process */}
          <section id="chargeback" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-cyan-400" />
              3. Chargeback Process
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">3.1 What is a Chargeback?</h3>
              <p>
                A chargeback occurs when a cardholder disputes a transaction with their issuing bank. The card network (Visa, Mastercard, etc.) then initiates a formal dispute process.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-6">3.2 Common Chargeback Reasons</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Unauthorized transaction (fraud)</li>
                <li>Product or service not received</li>
                <li>Product or service not as described</li>
                <li>Duplicate or incorrect charge</li>
                <li>Subscription cancellation not processed</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">3.3 Chargeback Procedure</h3>
              <ol className="list-decimal pl-6 space-y-2">
                <li>The cardholder files a dispute with their issuing bank</li>
                <li>The issuing bank submits the chargeback to the card network</li>
                <li>FluxPay receives the chargeback notification and notifies the merchant</li>
                <li>The merchant has a limited time to respond with evidence (representment)</li>
                <li>The card network reviews the evidence and makes a determination</li>
                <li>The outcome is communicated to all parties</li>
              </ol>

              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-200 text-sm">
                  <strong>Chargeback Ratio Thresholds:</strong> Card networks monitor chargeback ratios closely. FluxPay enforces the following thresholds:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-amber-200 text-sm">
                  <li><strong>Below 0.9%:</strong> Normal — no action required</li>
                  <li><strong>0.9% - 1.0%:</strong> Warning — enhanced monitoring activated, merchant notified</li>
                  <li><strong>1.0% - 1.5%:</strong> Elevated — settlement holds may be applied, remediation plan required</li>
                  <li><strong>1.5% - 2.0%:</strong> Critical — account restrictions, mandatory review with compliance team</li>
                  <li><strong>Above 2.0%:</strong> Termination risk — account may be suspended or terminated per card network rules</li>
                </ul>
                <p className="text-amber-200 text-sm mt-2">
                  Visa&apos;s Dispute Monitoring Program (VDMP) and Mastercard&apos;s Excessive Chargeback Program (ECP) impose additional fines and requirements on merchants exceeding network thresholds.
                </p>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">3.4 Chargeback Fees</h3>
              <p>
                A chargeback fee is assessed for each chargeback received, regardless of the outcome. Chargeback fees are outlined in the merchant&apos;s pricing agreement with FluxPay.
              </p>
            </div>
          </section>

          {/* Section 4 - Timeframes */}
          <section id="timeframes" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              4. Timeframes
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">4.1 Refund Processing</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Action</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Merchant-initiated refund</td>
                      <td className="py-3 px-4">5-10 business days to appear on cardholder statement</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Refund to original payment method</td>
                      <td className="py-3 px-4">Dependent on issuing bank processing times</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Refund confirmation</td>
                      <td className="py-3 px-4">Immediate notification via dashboard</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">4.2 Card Network Dispute Windows</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Card Network</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Dispute Filing Window</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Merchant Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Visa</td>
                      <td className="py-3 px-4">120 days from transaction date (75 days for certain reason codes)</td>
                      <td className="py-3 px-4">30 days from notification</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Mastercard</td>
                      <td className="py-3 px-4">120 days from transaction date</td>
                      <td className="py-3 px-4">45 days from notification</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">American Express</td>
                      <td className="py-3 px-4">120 days from transaction date</td>
                      <td className="py-3 px-4">20 days from notification</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium text-foreground mt-6">4.3 General Chargeback Timelines</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border dark:border-white/[0.1]">
                      <th className="text-left py-3 px-4 text-foreground font-medium">Stage</th>
                      <th className="text-left py-3 px-4 text-foreground font-medium">Timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Cardholder dispute filing</td>
                      <td className="py-3 px-4">Typically within 120 days of transaction</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Merchant notification</td>
                      <td className="py-3 px-4">Within 1-2 business days of receiving chargeback</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Merchant response window</td>
                      <td className="py-3 px-4">Typically 7-14 days (varies by card network)</td>
                    </tr>
                    <tr className="border-b border-border dark:border-white/[0.06]">
                      <td className="py-3 px-4 text-cyan-400">Resolution</td>
                      <td className="py-3 px-4">30-90 days from dispute filing</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Section 5 - Disputed Transactions */}
          <section id="disputes" className="mb-12">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              5. Disputed Transactions
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-medium text-foreground">5.1 For Cardholders</h3>
              <p>If you believe a transaction is unauthorized or incorrect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Contact the merchant first to attempt to resolve the issue directly</li>
                <li>If the merchant cannot resolve the issue, contact your issuing bank to initiate a dispute</li>
                <li>Provide your bank with any relevant documentation (receipts, correspondence, etc.)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">5.2 For Merchants</h3>
              <p>To respond to a chargeback effectively:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Respond promptly within the specified timeframe</li>
                <li>Provide compelling evidence (delivery confirmation, customer correspondence, signed agreements, IP logs, etc.)</li>
                <li>Maintain detailed transaction records for dispute resolution</li>
                <li>Use FluxPay&apos;s chargeback management tools in the merchant dashboard</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-6">5.3 Prevention</h3>
              <p>FluxPay provides merchants with tools to minimize chargebacks:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Clear billing descriptors that customers recognize</li>
                <li>Automated transaction receipts and notifications</li>
                <li>Fraud detection and prevention tools</li>
                <li>Transaction monitoring and alerts</li>
              </ul>
            </div>
          </section>

          {/* Section 6 - Contact */}
          <section id="contact" className="mb-12 p-6 rounded-2xl bg-card/80 dark:bg-white/[0.02] border border-border dark:border-white/[0.06]">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" />
              6. Contact
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>For questions about refunds, chargebacks, or disputed transactions:</p>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground">For Cardholders / End Customers</h3>
                  <div className="space-y-2 mt-2">
                    <p>
                      If you have questions about a charge on your statement, please contact the merchant from whom you made the purchase. The merchant name appears on your statement as the billing descriptor.
                    </p>
                    <p>
                      If you cannot identify the charge or reach the merchant, contact us:
                    </p>
                    <p>Email: support@fluxpay.online</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground">For Merchants</h3>
                  <div className="space-y-2 mt-2">
                    <p><strong>FluxPay</strong></p>
                    <p>Ideation House Financial Services Limited</p>
                    <p>3080 Yonge Street, Suite 6060, Toronto, Ontario, M4N 3N1, Canada</p>
                    <p>Email: support@fluxpay.online</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="pt-8 border-t border-border dark:border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <Link href="/terms" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Terms of Service
            </Link>
            <span className="text-gray-600">&middot;</span>
            <Link href="/privacy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              Privacy Policy
            </Link>
            <span className="text-gray-600">&middot;</span>
            <Link href="/aml-policy" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm">
              AML/KYC Policy
            </Link>
          </div>
          <div className="flex justify-end">
            <Link href="/auth/signin" className="px-6 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              Back to Sign In
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}
